/**
 * Pull a human-readable reason out of a FastAPI error body's `detail`.
 *
 * The API sends `detail` in two shapes: a typed object for everything it raises
 * deliberately (`{type, message?}`) and a bare string from the generic 500 handler
 * (`'internal_server_error'`). Interpolating the object form into a template string
 * rendered "Error 429: [object Object]" to the user, so prefer `message`, fall back to
 * the `type` slug, and only then to a placeholder.
 */
type ErrorDetailObject = { message?: string; type?: string }
type ValidationDetail = { msg?: string; message?: string; type?: string }

export type ErrorDetailLike =
  | string
  | ErrorDetailObject
  | ValidationDetail[]
  | null
  | undefined

export function httpErrorReason(
  detail: ErrorDetailLike,
  fallback = 'Unknown error'
): string {
  if (typeof detail === 'string') {
    return detail || fallback
  }
  if (Array.isArray(detail)) {
    // FastAPI's RequestValidationError sends detail as a list of {loc, msg, type}, so
    // a 422 arrives here as an array. `msg` carries the readable half ("field required").
    const reasons = detail
      .map((entry) => entry?.msg || entry?.message || entry?.type)
      .filter((reason): reason is string => Boolean(reason))
    return reasons.length ? reasons.join('; ') : fallback
  }
  return detail?.message || detail?.type || fallback
}

export default httpErrorReason
